<template>
  <Dialog :open="open" @update:open="handleClose" title="Delete API Key" size="lg">
    <div class="w-full" data-testid="confirm-delete-modal">
      <div class="space-y-4">
        <p class="text-sm text-gray-600">
          Are you sure you want to delete this API key? This action cannot be undone and will
          immediately invalidate the key.
        </p>

        <!-- API Key Details -->
        <Card v-if="apiKey" padding="sm">
          <div class="space-y-2">
            <div>
              <span class="text-sm font-medium text-gray-700">Name:</span>
              <span class="ml-2 text-sm text-gray-900">{{ apiKey.name }}</span>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Key:</span>
              <code
                class="ml-2 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 px-2 py-1 rounded border dark:border-gray-700 font-mono"
                >{{ apiKey.key_prefix }}****</code
              >
            </div>
            <div>
              <span class="text-sm font-medium text-gray-700">Created:</span>
              <span class="ml-2 text-sm text-gray-900">{{ formatDate(apiKey.created_at) }}</span>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-700">Type:</span>
              <span class="ml-2 text-sm text-gray-900">{{ apiKey.key_type === 'scope' ? (apiKey.scope || 'scope') : 'Fine-grained' }}</span>
            </div>
          </div>
        </Card>

        <!-- Warning Messages -->
        <div class="bg-red-50 border border-red-200 rounded-md p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">This action is permanent</h3>
              <div class="mt-2 text-sm text-red-700">
                <ul class="list-disc list-inside space-y-1">
                  <li>The API key will be immediately invalidated</li>
                  <li>Any applications using this key will stop working</li>
                  <li>This action cannot be undone</li>
                  <li>You'll need to create a new key if you need API access again</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Confirmation Input -->
        <div v-if="requireConfirmation">
          <label for="confirm-name" class="block text-sm font-medium text-gray-700 mb-2">
            To confirm, type "<strong>{{ apiKey?.name }}</strong
            >" below:
          </label>
          <Input
            id="confirm-name"
            v-model="confirmationName"
            type="text"
            placeholder="Enter the API key name"
            class="w-full"
            :error="confirmationError"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <div class="sm:flex sm:flex-row-reverse">
        <Button
          variant="danger"
          @click="handleDelete"
          :loading="isLoading"
          :disabled="!canDelete"
          class="w-full inline-flex justify-center sm:ml-3 sm:w-auto"
          data-testid="confirm-delete-button"
        >
          {{ isLoading ? 'Deleting...' : 'Delete API Key' }}
        </Button>
        <Button
          variant="outline"
          @click="handleClose"
          :disabled="isLoading"
          class="mt-3 w-full inline-flex justify-center sm:mt-0 sm:w-auto"
        >
          Cancel
        </Button>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { Dialog, Button, Input, Card, useNotification } from '@asabdev/asab-ui';
  import { useApiKeys } from '@/features/api-keys/composables';
  import { formatApiKeyDate } from '@/features/api-keys/utils';
  import type { ApiKey } from '@/features/api-keys/types';

  interface Props {
    open: boolean;
    apiKey?: ApiKey | null;
    requireConfirmation?: boolean;
  }

  interface Emits {
    (e: 'update:open', value: boolean): void;
    (e: 'deleted', apiKey: ApiKey): void;
  }

  const props = withDefaults(defineProps<Props>(), {
    apiKey: null,
    requireConfirmation: true,
  });

  const emit = defineEmits<Emits>();

  const { deleteApiKey, isLoading } = useApiKeys();
  const { showError } = useNotification();

  // Local state
  const confirmationName = ref('');
  const confirmationError = ref('');

  const canDelete = computed(() => {
    if (!props.apiKey) return false;
    if (!props.requireConfirmation) return true;

    const trimmedName = confirmationName.value.trim();
    return trimmedName === props.apiKey.name;
  });

  // Watchers
  watch(
    () => props.open,
    isOpen => {
      if (isOpen) {
        resetState();
      }
    }
  );

  watch(
    () => confirmationName.value,
    () => {
      confirmationError.value = '';
    }
  );

  // Methods
  const resetState = () => {
    confirmationName.value = '';
    confirmationError.value = '';
  };

  const formatDate = (dateString: string) => formatApiKeyDate(dateString);

  const handleDelete = async () => {
    if (!props.apiKey) return;

    if (props.requireConfirmation && !canDelete.value) {
      confirmationError.value = 'Please enter the correct API key name to confirm deletion';
      return;
    }

    try {
      const success = await deleteApiKey(props.apiKey.id);
      if (success) {
        emit('deleted', props.apiKey);
        handleClose();
      } else {
        showError('Failed to delete API key');
      }
    } catch (error) {
      showError((error as Error).message || 'Failed to delete API key');
    }
  };

  const handleClose = () => {
    emit('update:open', false);
  };
</script>
